import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const getProductsList = createAsyncThunk(
    'products/getProductsList',
    async ({ accountId, token, search, start, length, categoryId, supplierId, active }, { rejectWithValue }) => {
    try {
        let url = `${import.meta.env.VITE_API_BASE_URL}/products/list?accountId=${accountId}&start=${start}&length=${length}&draw=1`;

        // Aggiungo i parametri di ricerca se presenti
        if (search && search.length > 0) {
            url += `&search[value]=${encodeURIComponent(search)}`;
        } else {
            url += `&search[value]=`;
        }

        // Aggiungo filtri opzionali
        if (categoryId) {
            url += `&categoryId=${categoryId}`;
        }

        if (supplierId) {
            url += `&supplierId=${supplierId}`;
        }

        if (active !== undefined) {
            url += `&active=${active}`;
        }

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei prodotti');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const getProductDetails = createAsyncThunk(
    'products/getProductDetails',
    async ({ productId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/products?productId=${productId}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei dettagli del prodotto');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const createProduct = createAsyncThunk(
    'products/createProduct',
    async ({ product, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/products`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ product })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante la creazione del prodotto');
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const updateProduct = createAsyncThunk(
    'products/updateProduct',
    async ({ product, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/products`;
        const response = await fetch(url, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ product })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante l\'aggiornamento del prodotto');
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const deleteProduct = createAsyncThunk(
    'products/deleteProduct',
    async ({ productId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/products`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ productId })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante l\'eliminazione del prodotto');
        }
        return productId;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const getWarehousesList = createAsyncThunk(
    'products/getWarehousesList',
    async ({ accountId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/products/warehousesList?accountId=${accountId}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei magazzini');
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const getRelatedProducts = createAsyncThunk(
    'products/getRelatedProducts',
    async ({ productId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/products/resolveAccessories?productId=${productId}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei prodotti correlati');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const getReverseAccessories = createAsyncThunk(
    'products/getReverseAccessories',
    async ({ productId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/products/reverseAccessories?productId=${productId}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero delle correlazioni inverse');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const moveProductToCategory = createAsyncThunk(
    'products/moveProductToCategory',
    async ({ productId, sourceCategoryId, targetCategoryId, targetCategoryName, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/products/moveToCategory`;
        const response = await fetch(url, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ productId, sourceCategoryId, targetCategoryId, targetCategoryName })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante lo spostamento del prodotto');
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const getProductCategories = createAsyncThunk(
    'products/getProductCategories',
    async ({ accountId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/products/categoriesTree?accountId=${accountId}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero delle categorie prodotti');
        }

        const data = await response.json();
        // categoriesTree restituisce l'array direttamente (non wrappato in data)
        return Array.isArray(data) ? data : (data.data || []);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

const productsSlice = createSlice({
    name: 'products',
    initialState: {
        data: null,
        loading: false,
        error: null,
        productDetails: null,
        loadingDetails: false,
        errorDetails: null,
        categories: [],
        loadingCategories: false,
        errorCategories: null,
        createLoading: false,
        createError: null,
        updateLoading: false,
        updateError: null,
        deleteLoading: false,
        deleteError: null,
        warehouses: [],
        warehousesLoading: false,
        warehousesError: null,
        relatedProducts: [],
        relatedProductsLoading: false,
        reverseAccessories: [],
        reverseAccessoriesLoading: false,
        moveLoading: false,
        moveError: null
    },
    reducers: {
        resetProductDetails: (state) => {
            state.productDetails = null;
            state.loadingDetails = false;
            state.errorDetails = null;
        },
        resetRelatedProducts: (state) => {
            state.relatedProducts = [];
            state.relatedProductsLoading = false;
            state.reverseAccessories = [];
            state.reverseAccessoriesLoading = false;
        }
    },
    extraReducers: (builder) => {
        builder
            // getProductsList
            .addCase(getProductsList.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getProductsList.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(getProductsList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Errore durante il recupero dei prodotti';
            })
            // getProductDetails
            .addCase(getProductDetails.pending, (state) => {
                state.loadingDetails = true;
                state.errorDetails = null;
            })
            .addCase(getProductDetails.fulfilled, (state, action) => {
                state.loadingDetails = false;
                state.productDetails = action.payload;
            })
            .addCase(getProductDetails.rejected, (state, action) => {
                state.loadingDetails = false;
                state.errorDetails = action.payload || 'Errore durante il recupero dei dettagli del prodotto';
            })
            // createProduct
            .addCase(createProduct.pending, (state) => {
                state.createLoading = true;
                state.createError = null;
            })
            .addCase(createProduct.fulfilled, (state) => {
                state.createLoading = false;
            })
            .addCase(createProduct.rejected, (state, action) => {
                state.createLoading = false;
                state.createError = action.payload;
            })
            // updateProduct
            .addCase(updateProduct.pending, (state) => {
                state.updateLoading = true;
                state.updateError = null;
            })
            .addCase(updateProduct.fulfilled, (state) => {
                state.updateLoading = false;
            })
            .addCase(updateProduct.rejected, (state, action) => {
                state.updateLoading = false;
                state.updateError = action.payload;
            })
            // deleteProduct
            .addCase(deleteProduct.pending, (state) => {
                state.deleteLoading = true;
                state.deleteError = null;
            })
            .addCase(deleteProduct.fulfilled, (state) => {
                state.deleteLoading = false;
            })
            .addCase(deleteProduct.rejected, (state, action) => {
                state.deleteLoading = false;
                state.deleteError = action.payload;
            })
            // getWarehousesList
            .addCase(getWarehousesList.pending, (state) => {
                state.warehousesLoading = true;
                state.warehousesError = null;
            })
            .addCase(getWarehousesList.fulfilled, (state, action) => {
                state.warehousesLoading = false;
                state.warehouses = action.payload;
            })
            .addCase(getWarehousesList.rejected, (state, action) => {
                state.warehousesLoading = false;
                state.warehousesError = action.payload;
            })
            // getRelatedProducts
            .addCase(getRelatedProducts.pending, (state) => {
                state.relatedProductsLoading = true;
            })
            .addCase(getRelatedProducts.fulfilled, (state, action) => {
                state.relatedProductsLoading = false;
                state.relatedProducts = action.payload;
            })
            .addCase(getRelatedProducts.rejected, (state) => {
                state.relatedProductsLoading = false;
                state.relatedProducts = [];
            })
            // getReverseAccessories
            .addCase(getReverseAccessories.pending, (state) => {
                state.reverseAccessoriesLoading = true;
            })
            .addCase(getReverseAccessories.fulfilled, (state, action) => {
                state.reverseAccessoriesLoading = false;
                state.reverseAccessories = action.payload;
            })
            .addCase(getReverseAccessories.rejected, (state) => {
                state.reverseAccessoriesLoading = false;
                state.reverseAccessories = [];
            })
            // moveProductToCategory
            .addCase(moveProductToCategory.pending, (state) => {
                state.moveLoading = true;
                state.moveError = null;
            })
            .addCase(moveProductToCategory.fulfilled, (state) => {
                state.moveLoading = false;
            })
            .addCase(moveProductToCategory.rejected, (state, action) => {
                state.moveLoading = false;
                state.moveError = action.payload || 'Errore durante lo spostamento del prodotto';
            })
            // getProductCategories
            .addCase(getProductCategories.pending, (state) => {
                state.loadingCategories = true;
                state.errorCategories = null;
            })
            .addCase(getProductCategories.fulfilled, (state, action) => {
                state.loadingCategories = false;
                state.categories = action.payload;
            })
            .addCase(getProductCategories.rejected, (state, action) => {
                state.loadingCategories = false;
                state.errorCategories = action.payload || 'Errore durante il recupero delle categorie prodotti';
            });
    }
});

export const { resetProductDetails, resetRelatedProducts } = productsSlice.actions;

export const selectProductsData = (state) => state.products.data;
export const selectProductsLoading = (state) => state.products.loading;
export const selectProductsError = (state) => state.products.error;
export const selectProductDetails = (state) => state.products.productDetails;
export const selectProductDetailsLoading = (state) => state.products.loadingDetails;
export const selectProductDetailsError = (state) => state.products.errorDetails;
export const selectProductCategories = (state) => state.products.categories;
export const selectProductCategoriesLoading = (state) => state.products.loadingCategories;
export const selectProductCategoriesError = (state) => state.products.errorCategories;

export const selectProductCreateLoading = (state) => state.products.createLoading;
export const selectProductCreateError = (state) => state.products.createError;
export const selectProductUpdateLoading = (state) => state.products.updateLoading;
export const selectProductUpdateError = (state) => state.products.updateError;
export const selectProductDeleteLoading = (state) => state.products.deleteLoading;
export const selectProductDeleteError = (state) => state.products.deleteError;
export const selectWarehouses = (state) => state.products.warehouses;
export const selectWarehousesLoading = (state) => state.products.warehousesLoading;
export const selectRelatedProducts = (state) => state.products.relatedProducts;
export const selectRelatedProductsLoading = (state) => state.products.relatedProductsLoading;
export const selectReverseAccessories = (state) => state.products.reverseAccessories;
export const selectReverseAccessoriesLoading = (state) => state.products.reverseAccessoriesLoading;
export const selectMoveLoading = (state) => state.products.moveLoading;
export const selectMoveError = (state) => state.products.moveError;

export default productsSlice.reducer;
